import React, { useState } from 'react';
import { Plus, Trash2, Package, Search } from 'lucide-react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const LineItemsTable = ({ items, productItems, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Convert product items to select options
  const productOptions = productItems?.map(item => ({
    value: item?.id?.toString(),
    label: item?.name,
    description: item?.description,
    rate: item?.rate,
    taxRate: item?.taxRate
  }));

  const calculateItemAmount = (quantity, rate) => {
    return (parseFloat(quantity) || 0) * (parseFloat(rate) || 0);
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems?.[index], [field]: value };

    // Recalculate amount when quantity or rate changes
    if (field === 'quantity' || field === 'rate') {
      updatedItems[index].amount = calculateItemAmount(
        updatedItems?.[index]?.quantity,
        updatedItems?.[index]?.rate
      );
    }

    onChange(updatedItems);
  };

  const selectProduct = (index, productId) => {
    const product = productItems?.find(p => p?.id?.toString() === productId);
    if (product) {
      const updatedItems = [...items];
      updatedItems[index] = {
        ...updatedItems?.[index],
        description: product?.description,
        rate: product?.rate,
        taxRate: product?.taxRate,
        amount: calculateItemAmount(updatedItems?.[index]?.quantity, product?.rate)
      };
      onChange(updatedItems);
    }
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      rate: 0,
      taxRate: 0,
      amount: 0
    };
    onChange([...items, newItem]);
  };

  const removeItem = (index) => {
    if (items?.length > 1) {
      const updatedItems = items?.filter((_, i) => i !== index);
      onChange(updatedItems);
    }
  };

  const duplicateItem = (index) => {
    const itemToDuplicate = { ...items?.[index], id: Date.now() };
    const updatedItems = [...items];
    updatedItems?.splice(index + 1, 0, itemToDuplicate);
    onChange(updatedItems);
  };

  return (
    <div className="bg-white rounded-lg border">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Line Items</h2>
          </div>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={addItem}
          >
            Add Item
          </Button>
        </div>

        {/* Quick Product Search */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products and services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="pl-10"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-8">#</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 min-w-80">
                Item & Description
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-24">
                Qty
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-32">
                Rate ($)
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-24">
                Tax (%)
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 w-32">
                Amount ($)
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 w-16">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items?.map((item, index) => (
              <tr key={item?.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-600">
                  {index + 1}
                </td>
                
                {/* Item & Description */}
                <td className="px-4 py-3 space-y-2">
                  <Select
                    placeholder="Select product/service..."
                    options={productOptions}
                    value=""
                    onChange={(value) => selectProduct(index, value)}
                    searchable={true}
                    className="w-full"
                  />
                  <textarea
                    value={item?.description}
                    onChange={(e) => updateItem(index, 'description', e?.target?.value)}
                    placeholder="Enter item description..."
                    className="w-full p-2 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows="2"
                  />
                </td>

                {/* Quantity */}
                <td className="px-4 py-3">
                  <Input
                    type="number"
                    value={item?.quantity}
                    onChange={(e) => updateItem(index, 'quantity', e?.target?.value)}
                    min="0"
                    step="0.01"
                    className="w-full text-center"
                  />
                </td>

                {/* Rate */}
                <td className="px-4 py-3">
                  <Input
                    type="number"
                    value={item?.rate}
                    onChange={(e) => updateItem(index, 'rate', e?.target?.value)}
                    min="0"
                    step="0.01"
                    className="w-full text-right"
                  />
                </td>

                {/* Tax Rate */}
                <td className="px-4 py-3">
                  <select
                    value={item?.taxRate}
                    onChange={(e) => updateItem(index, 'taxRate', e?.target?.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="0">0%</option>
                    <option value="5">5%</option>
                    <option value="10">10%</option>
                    <option value="15">15%</option>
                    <option value="18">18%</option>
                    <option value="25">25%</option>
                  </select>
                </td>

                {/* Amount */}
                <td className="px-4 py-3 text-right">
                  <div className="font-medium text-gray-900">
                    ${item?.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </td>

                {/* Actions */}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => duplicateItem(index)}
                      title="Duplicate item"
                      iconName="Copy"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => removeItem(index)}
                      disabled={items?.length === 1}
                      title="Remove item"
                      iconName="Trash2"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Add Item Button */}
      <div className="p-4 border-t bg-gray-50">
        <Button
          variant="outline"
          iconName="Plus"
          iconPosition="left"
          onClick={addItem}
          className="w-full sm:w-auto"
        >
          Add Another Item
        </Button>
      </div>
    </div>
  );
};

export default LineItemsTable;